import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Search, Eye, RotateCcw, Trash2, DollarSign, Undo2 } from "lucide-react";
import { money, fechaMX, fechaHoraMX } from "@/lib/format";
import { useNavigate } from "react-router-dom";
import ReporteModulo from "@/components/ReporteModulo";
import FiltroSelect, { labelDe } from "@/components/FiltroSelect";
import RemisionArchivos, { RemisionBotones } from "@/components/RemisionArchivos";
import { descargarFactura } from "@/components/FacturaArchivos";
import { useVendedoresActivos } from "@/hooks/useVendedoresActivos";

const pagoStatus = (p) => {
  if (p.estatus_pago === "Cobrado" || p.fecha_pago) return "Cobrado";
  if (p.estatus_pago === "Pendiente de cobro" || p.factura || p.fecha_facturacion) return "Pendiente de cobro";
  return "Sin facturar";
};
const pagoBadge = (s) => (s === "Cobrado" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-transparent" : s === "Pendiente de cobro" ? "bg-amber-100 text-amber-700 hover:bg-amber-100 border-transparent" : "bg-slate-100 text-slate-600 hover:bg-slate-100 border-transparent");
const fechaHora = (iso) => fechaHoraMX(iso);

const PAGO_EST = ["Cobrado", "Pendiente de cobro", "Sin facturar"];
const histVend = (p) => p.vendedor?.full_name;
const cierreFecha = (p) => p.fecha_cierre || (p.created_at ? String(p.created_at).slice(0, 10) : null);

export default function HistorialPedidos() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  const [timeline, setTimeline] = useState({ vida: [], cambios: [] });
  const [reabrirSel, setReabrirSel] = useState(null);
  const [eliminarSel, setEliminarSel] = useState(null);
  const [revertirSel, setRevertirSel] = useState(null);
  const [vendSel, setVendSel] = useState("todos");
  const [pagoSel, setPagoSel] = useState("todos");
  const vendedores = useVendedoresActivos(isAdmin, pedidos, histVend);
  const visibles = useMemo(() => pedidos.filter((p) => (!isAdmin || vendSel === "todos" || (histVend(p) || "") === vendSel) && (pagoSel === "todos" || pagoStatus(p) === pagoSel) && (!search || `${p.numero_pedido} ${p.cliente}`.toLowerCase().includes(search.toLowerCase()))), [pedidos, vendSel, pagoSel, search, isAdmin]);

  useEffect(() => {
    api.get("/pedidos").then(({ data }) => setPedidos((data || []).filter((p) => p.estatus === "Pedido cerrado"))).finally(() => setLoading(false));
  }, []);

  const verDetalle = async (p) => {
    setSel(p);
    setTimeline({ vida: [], cambios: [] });
    try { const { data } = await api.get(`/pedidos/${p.id}/timeline`); setTimeline(data || { vida: [], cambios: [] }); } catch { /* noop */ }
  };

  const reabrir = async (p) => {
    let estatus = "Producción";
    if (p.fecha_pago) estatus = "Pago";
    else if (p.fecha_vencimiento) estatus = "Vencimiento";
    else if (p.fecha_facturacion) estatus = "Facturación";
    else if (p.fecha_entrega) estatus = "Entregado";
    try {
      await api.put(`/pedidos/${p.id}`, { estatus });
      setPedidos((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Pedido reabierto. Volvió al flujo activo de Pedidos.");
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
    finally { setReabrirSel(null); }
  };

  const eliminar = async (p) => {
    try {
      await api.delete(`/pedidos/${p.id}`);
      setPedidos((prev) => prev.filter((x) => x.id !== p.id));
      toast.success("Registro eliminado del historial.");
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
    finally { setEliminarSel(null); }
  };

  const revertir = async (p) => {
    try {
      await api.post(`/pedidos/${p.id}/revertir`);
      setPedidos((prev) => prev.filter((x) => x.id !== p.id));
      toast.success(`Pedido ${p.numero_pedido} revertido. La cotización volvió a "Pendiente de autorización".`);
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
    finally { setRevertirSel(null); }
  };

  const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
  const grupos = useMemo(() => {
    const now = new Date();
    const curY = now.getFullYear();
    const curM = now.getMonth();
    const filtrados = visibles;
    const sortDesc = (a, b) => (cierreFecha(b) || "").localeCompare(cierreFecha(a) || "");
    const mesEnCurso = [], mesesAnio = {}, porAnio = {}, sinFecha = [];
    filtrados.forEach((p) => {
      const f = cierreFecha(p);
      const d = f ? new Date(f + "T00:00:00") : null;
      if (!d || isNaN(d)) { sinFecha.push(p); return; }
      const y = d.getFullYear(), m = d.getMonth();
      if (y === curY && m === curM) mesEnCurso.push(p);
      else if (y === curY) (mesesAnio[m] = mesesAnio[m] || []).push(p);
      else if (y < curY) { (porAnio[y] = porAnio[y] || {}); (porAnio[y][m] = porAnio[y][m] || []).push(p); }
      else (mesesAnio[m] = mesesAnio[m] || []).push(p);
    });
    mesEnCurso.sort(sortDesc); sinFecha.sort(sortDesc);
    Object.values(mesesAnio).forEach((a) => a.sort(sortDesc));
    Object.values(porAnio).forEach((mm) => Object.values(mm).forEach((a) => a.sort(sortDesc)));
    return { mesEnCurso, mesesAnio, porAnio, sinFecha, curY };
  }, [visibles]);
  const mesesAnioKeys = Object.keys(grupos.mesesAnio).map(Number).sort((a, b) => a - b);
  const countMesesAnio = mesesAnioKeys.reduce((s, m) => s + grupos.mesesAnio[m].length, 0);
  const aniosKeys = Object.keys(grupos.porAnio).sort((a, b) => String(b).localeCompare(String(a)));
  const countAnios = aniosKeys.reduce((s, y) => s + Object.values(grupos.porAnio[y]).reduce((t, a) => t + a.length, 0), 0);

  const Seccion = ({ titulo, rows }) => (
    <div className="space-y-2" data-testid={`historial-seccion-${titulo.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold">{titulo}</h3>
        <Badge variant="secondary">{rows.length}</Badge>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground pb-2">No hay pedidos cerrados en este periodo.</p>
      ) : (
        <div className="border rounded-md overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Pedido</TableHead><TableHead>Cliente</TableHead><TableHead>Fecha de cierre</TableHead><TableHead>Pago</TableHead><TableHead className="text-right">Subtotal</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((p) => {
                const ps = pagoStatus(p);
                return (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono text-xs font-semibold">{p.numero_pedido}</TableCell>
                    <TableCell className="font-medium">{p.cliente}</TableCell>
                    <TableCell>{fechaMX(cierreFecha(p), "—")}</TableCell>
                    <TableCell><Badge className={pagoBadge(ps)}>{ps}</Badge></TableCell>
                    <TableCell className="text-right">{money(p.subtotal)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="icon" title="Ver detalle" onClick={() => verDetalle(p)} data-testid={`historial-ver-${p.id}`}><Eye className="h-4 w-4" /></Button>
                        {isAdmin && <Button variant="outline" size="icon" title="Gestionar comisión" onClick={() => navigate("/comisiones", { state: { comisionPedidoId: p.id } })} data-testid={`historial-comision-${p.id}`}><DollarSign className="h-4 w-4" /></Button>}
                        {isAdmin && <Button variant="outline" size="icon" title="Reabrir pedido" onClick={() => setReabrirSel(p)} data-testid={`historial-reabrir-${p.id}`}><RotateCcw className="h-4 w-4" /></Button>}
                        {isAdmin && p.cotizacion_id && <Button variant="outline" size="icon" title="Revertir a cotización" className="text-amber-600" onClick={() => setRevertirSel(p)} data-testid={`historial-revertir-${p.id}`}><Undo2 className="h-4 w-4" /></Button>}
                        {isAdmin && <Button variant="outline" size="icon" className="text-red-600 hover:text-red-700 hover:bg-red-50" title="Eliminar registro" onClick={() => setEliminarSel(p)} data-testid={`historial-eliminar-${p.id}`}><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="historial-pedidos-page">
      <div><h1 className="text-3xl font-bold tracking-tight">Historial de pedidos</h1><p className="text-muted-foreground mt-2">Pedidos marcados como cerrados, organizados por periodo.</p></div>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="relative flex-1 max-w-lg">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input type="search" placeholder="Buscar por pedido o cliente..." className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="historial-search" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
            {isAdmin && <FiltroSelect value={vendSel} onChange={setVendSel} todosLabel="Todos los vendedores" options={vendedores} testid="historial-filtro-vendedor" />}
            <FiltroSelect value={pagoSel} onChange={setPagoSel} todosLabel="Todos los pagos" options={PAGO_EST} testid="historial-filtro-pago" />
            {isAdmin && (
            <ReporteModulo
              titulo="REPORTE DE HISTORIAL DE PEDIDOS"
              filenameBase="Reporte historial pedidos"
              prefix="rep-hist"
              triggerTestId="historial-reporte-btn"
              buttonVariant="outline"
              rows={visibles}
              getFecha={cierreFecha}
              groupBy={(p) => p.vendedor?.full_name || "Sin asignar"}
              filtros={[{ label: "Vendedor", value: labelDe(vendSel, vendedores) }, { label: "Pago", value: labelDe(pagoSel, PAGO_EST) }, { label: "Búsqueda", value: search }]}
              columns={[
                { header: "Pedido", value: (p) => p.numero_pedido, mono: true },
                { header: "Cliente", value: (p) => p.cliente },
                { header: "Fecha de cierre", value: (p) => fechaMX(cierreFecha(p)) },
                { header: "Pago", value: (p) => pagoStatus(p) },
                { header: "Subtotal", align: "right", value: (p) => money(p.subtotal) },
              ]}
              subtotal={(rows) => money(rows.reduce((s, p) => s + Number(p.subtotal || 0), 0))}
              totales={(rows) => [{ label: "Total subtotal", value: money(rows.reduce((s, p) => s + Number(p.subtotal || 0), 0)) }]}
            />
            )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <p className="text-center text-muted-foreground py-12">Cargando...</p>
          ) : (
            <>
              <Seccion titulo="Mes en curso" rows={grupos.mesEnCurso} />

              <Accordion type="multiple" className="border rounded-md divide-y">
                <AccordionItem value="meses-anio" className="border-0 px-4">
                  <AccordionTrigger data-testid="acc-meses-anteriores" className="hover:no-underline">
                    <span className="flex items-center gap-2"><span className="text-lg font-semibold">Meses anteriores ({grupos.curY})</span><Badge variant="secondary">{countMesesAnio}</Badge></span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-6 pt-2">
                    {mesesAnioKeys.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No hay pedidos cerrados en meses anteriores de este año.</p>
                    ) : (
                      mesesAnioKeys.map((m) => <Seccion key={m} titulo={`${MESES[m]} ${grupos.curY}`} rows={grupos.mesesAnio[m]} />)
                    )}
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="anios" className="border-0 px-4">
                  <AccordionTrigger data-testid="acc-anios-anteriores" className="hover:no-underline">
                    <span className="flex items-center gap-2"><span className="text-lg font-semibold">Años anteriores</span><Badge variant="secondary">{countAnios}</Badge></span>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2">
                    {aniosKeys.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No hay pedidos cerrados de años anteriores.</p>
                    ) : (
                      <Accordion type="multiple" className="border rounded-md divide-y">
                        {aniosKeys.map((y) => {
                          const mesesKeys = Object.keys(grupos.porAnio[y]).map(Number).sort((a, b) => a - b);
                          const countY = mesesKeys.reduce((s, m) => s + grupos.porAnio[y][m].length, 0);
                          return (
                            <AccordionItem key={y} value={y} className="border-0 px-4">
                              <AccordionTrigger data-testid={`acc-anio-${y}`} className="hover:no-underline">
                                <span className="flex items-center gap-2"><span className="font-semibold">{y}</span><Badge variant="secondary">{countY}</Badge></span>
                              </AccordionTrigger>
                              <AccordionContent className="space-y-6 pt-2">
                                {mesesKeys.map((m) => <Seccion key={m} titulo={`${MESES[m]} ${y}`} rows={grupos.porAnio[y][m]} />)}
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {grupos.sinFecha.length > 0 && <Seccion titulo="Sin fecha" rows={grupos.sinFecha} />}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pedido {sel?.numero_pedido}</DialogTitle>
            <DialogDescription>{sel?.cliente} · Subtotal {money(sel?.subtotal)}</DialogDescription>
          </DialogHeader>
          {isAdmin && sel && (
            <div><RemisionArchivos pedido={sel} estatusGuardado={sel.estatus} onChange={(d) => { setSel((s) => ({ ...s, remisiones: d.remisiones, remision_enviada_at: d.remision_enviada_at, historial: d.historial })); setPedidos((prev) => prev.map((x) => (x.id === d.id ? { ...x, ...d } : x))); api.get(`/pedidos/${d.id}/timeline`).then(({ data: t }) => setTimeline(t || { vida: [], cambios: [] })).catch(() => {}); }} /></div>
          )}
          {(sel?.remisiones?.length > 0 || sel?.factura_pdf || sel?.factura_xml) && (
            <div className="flex flex-wrap gap-2" data-testid="historial-expediente">
              <RemisionBotones pedido={sel} />
              {sel?.factura_pdf && <Button variant="outline" size="sm" className="text-blue-700 border-blue-700 hover:bg-blue-50" onClick={() => descargarFactura(sel, "pdf")} data-testid="historial-factura-pdf">Factura PDF</Button>}
              {sel?.factura_xml && <Button variant="outline" size="sm" className="text-blue-700 border-blue-700 hover:bg-blue-50" onClick={() => descargarFactura(sel, "xml")} data-testid="historial-factura-xml">Factura XML</Button>}
            </div>
          )}
          <div className="space-y-6">
            <div data-testid="historial-vida">
              <h4 className="font-semibold text-sm mb-3">Vida del pedido</h4>
              <ol className="relative border-l border-slate-200 ml-2 space-y-4">
                {(timeline.vida || []).map((e, i) => (
                  <li key={i} className="ml-4">
                    <span className="absolute -left-[7px] mt-1 h-3 w-3 rounded-full bg-primary ring-4 ring-white" />
                    <div className="text-xs text-muted-foreground">{fechaMX(e.fecha, "—")}</div>
                    <div className="text-sm font-medium">{e.titulo}</div>
                    {e.detalle && <div className="text-xs text-muted-foreground">{e.detalle}</div>}
                  </li>
                ))}
              </ol>
            </div>
            <div className="border-t pt-4">
              <h4 className="font-semibold text-sm mb-3">Historial de cambios</h4>
              {(timeline.cambios || []).length === 0 ? (
                <p className="text-xs text-muted-foreground">Sin cambios registrados.</p>
              ) : (
                <ul className="space-y-2">
                  {(timeline.cambios || []).map((c, i) => (
                    <li key={i} className="text-xs flex flex-col sm:flex-row sm:gap-2">
                      <span className="text-muted-foreground whitespace-nowrap">{fechaHora(c.ts)}</span>
                      <span><span className="font-semibold">{c.campo}:</span> {c.de || "—"} → {c.a || "—"} <span className="text-muted-foreground">· {c.usuario}</span></span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!reabrirSel} onOpenChange={(o) => !o && setReabrirSel(null)}>
        <AlertDialogContent data-testid="reabrir-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Reabrir pedido {reabrirSel?.numero_pedido}?</AlertDialogTitle>
            <AlertDialogDescription>
              El pedido saldrá del historial y volverá al flujo activo de Pedidos para seguir gestionándolo. Se conservan sus datos y fechas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reabrir-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => reabrir(reabrirSel)} data-testid="reabrir-confirm">Sí, reabrir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!eliminarSel} onOpenChange={(o) => !o && setEliminarSel(null)}>
        <AlertDialogContent data-testid="eliminar-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar el pedido {eliminarSel?.numero_pedido}?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción es permanente y no se puede deshacer. Se eliminará el pedido junto con su remisión y comisión asociadas. ¿Deseas continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="eliminar-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => eliminar(eliminarSel)} className="bg-red-600 hover:bg-red-700" data-testid="eliminar-confirm">Sí, eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!revertirSel} onOpenChange={(o) => !o && setRevertirSel(null)}>
        <AlertDialogContent data-testid="revertir-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Revertir el pedido {revertirSel?.numero_pedido} a cotización?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el pedido, su remisión y su comisión, y la cotización volverá a "Pendiente de autorización". Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="revertir-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => revertir(revertirSel)} className="bg-amber-600 hover:bg-amber-700" data-testid="revertir-confirm">Sí, revertir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
